import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Profile from "./Profile";
import MainGame from "./MainGame";
import AppealText from "./AppealText";
import Affiliation from "./Affiliation";
import ActivityHistory from "./ActivityHistory";
import SinglePersonTitle from "./SinglePersonTitle";
import Works from "./Works";
import TeamMembers from "./TeamMembers";
import OfferButton from "../offer/OfferButton";
import OfferModalContainer from "../offer/OfferModalContainer";
import YouTubeChannelContainer from "../media/youtube/YouTubeChannelContainer";
import NoData from "../../common/NoData";
import SNSInformation from "../../common/sns/SNSInformation";
import VerificationIcon from "../../common/VerificationIcon";
import { SonicTermData } from "../../common/classes/SonicTerm";
import { SonicWorkData } from "../../common/classes/SonicWork";
import { SonicDBEntryData } from "../../common/classes/SonicDBEntry";
import styles from "./singleperson.module.scss";
import ContentReportButton from "../../common/report/ContentReportButton";
import ContentReportModalContainer from "../../common/report/ContentReportModalContainer";
import { PageTypes } from "../../utils/PageTypes";
import { SonicGameDetailData } from "../../common/classes/SonicGameDetail";
import GameDetails from "./GameDetails";
import SNSFollowersContainer from "./SNSFollowersContainer";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  id: string;
  owner: string;
  ownerName: string;
  ownerChatDenial: boolean;
  img: string;
  name: string;
  game?: Array<SonicTermData>;
  team?: SonicTermData;
  playerTypeName?: string;
  profile?: string;
  birthplace?: string;
  height?: string;
  weight?: string;
  hobby?: string;
  history?: Array<{ year?: string; title?: string }>;
  works?: Array<SonicWorkData>;
  twitter?: string;
  instagram?: string;
  facebook?: string;
  youtube?: string;
  twitch?: string;
  school?: SonicTermData;
  course?: SonicTermData;
  verified: boolean;
  isLoggedIn?: boolean;
  isUsersOwnDBEntry?: boolean;
  showOfferButton: boolean;
  members: SonicDBEntryData[];
  gameDetails: Array<SonicGameDetailData>;
};
const SinglePerson: React.FC<Props> = (props) => {
  const {
    id,
    owner,
    ownerName,
    ownerChatDenial,
    img,
    name,
    game,
    team,
    playerTypeName,
    profile,
    birthplace,
    height,
    weight,
    hobby,
    history,
    works,
    twitter,
    instagram,
    facebook,
    youtube,
    twitch,
    school,
    course,
    verified,
    isLoggedIn,
    isUsersOwnDBEntry,
    showOfferButton,
    members,
    gameDetails,
  } = props;

  const [isModalShown, setIsModalShown] = useState<boolean>(false);
  const [isReportModalShown, setIsReportModalShown] = useState<boolean>(false);

  return (
    <>
      <article className={styles["singlePerson"]}>
        <div className={styles["userInfo-wrapper"]}>
          <div className={styles["userInfo"]}>
            <div className={styles["userInfo__top"]}>
              <div className={styles["userInfo__top__front"]}>
                <div className={styles["userInfo__top__imgBox"]}>
                  <img src={img} />
                </div>
                <SonicLink
                  to={`/user/${owner}`}
                  className={styles["userDetail-btn"]}
                >
                  <FontAwesomeIcon icon={["fas", "user"]} />
                  <span>{ownerName}</span>
                </SonicLink>
              </div>
              <div
                className={styles["userInfo__top__bg"]}
                style={{
                  backgroundImage: `url(${img})`,
                }}
              ></div>
            </div>
            <div className={styles["userInfo__bottom"]}>
              <div className={styles["userInfo__bottom__head"]}>
                <h2 className={styles["userInfo__bottom__name"]}>
                  {name}
                  {verified && <VerificationIcon />}
                </h2>
                <div className={styles["userInfo__bottom__type"]}>
                  {playerTypeName}
                </div>
              </div>
              {showOfferButton == true && !isUsersOwnDBEntry ? (
                <div className={styles["offer-btn-wrapper"]}>
                  {isLoggedIn == true ? (
                    <OfferButton
                      isUserLoggedIn={true}
                      handler={() => {
                        setIsModalShown(true);
                      }}
                    />
                  ) : (
                    <OfferButton isUserLoggedIn={false} />
                  )}
                </div>
              ) : (
                <div className={styles["offer-btn-wrapper"]}>
                  <p className={styles["cant-offer-text"]}>
                    オファー不可
                    <br />
                    プロフィール
                  </p>
                </div>
              )}
              {twitter || instagram || facebook || youtube || twitch ? (
                <SNSInformation
                  twitter={twitter}
                  instagram={instagram}
                  facebook={facebook}
                  youtube={youtube}
                  twitch={twitch}
                />
              ) : null}
            </div>
          </div>
          <SonicLink to={`/person`} className={styles["backtolist-btn"]}>
            Back to List
          </SonicLink>
        </div>
        <div className={styles["introduction"]}>
          <ContentReportButton
            handler={() => setIsReportModalShown(true)}
            wrapperClass={styles["report-btn"]}
          />
          <span className={styles["scroll"]}>SCROLL</span>
          {gameDetails.length > 0 && game ? (
            <div className={styles["introduction__item"]}>
              <SinglePersonTitle title="PLAYING DATA" subTitle="ゲーム実績" />
              <div className={styles["introduction__item__contents"]}>
                <GameDetails myGames={game ?? []} details={gameDetails} />
              </div>
            </div>
          ) : null}
          <SinglePersonTitle title={"INTRODUCTION"} subTitle={"自己紹介"} />
          {profile ? (
            <div className={styles["introduction__item"]}>
              <div className={styles["introduction__item__contents"]}>
                <AppealText profile={profile} />
              </div>
            </div>
          ) : (
            <NoData text={"自己紹介の情報がありません"} />
          )}
          {works && works.length > 0 ? (
            <div className={styles["introduction__item"]}>
              <SinglePersonTitle
                title={"WORKS"}
                subTitle={"作品"}
                report={true}
              />
              <div className={styles["introduction__item__contents"]}>
                <Works works={works} />
              </div>
            </div>
          ) : null}

          <SNSFollowersContainer
            id={id}
            instagramLink={instagram}
            youtubeLink={youtube}
            twitchLink={twitch}
          />

          {youtube ? (
            <div className={styles["introduction__item"]}>
              <SinglePersonTitle title={"MEDIA"} subTitle={"メディア"} />
              <div className={styles["introduction__item__contents"]}>
                <div className={styles["media"]}>
                  <YouTubeChannelContainer
                    link={youtube}
                    pageType="profile"
                    contentId={id}
                  />
                </div>
              </div>
            </div>
          ) : null}
          {history && history.length > 0 ? (
            <div className={styles["introduction__item"]}>
              <SinglePersonTitle title={"HISTORY"} subTitle={"活動実績"} />
              <div className={styles["introduction__item__contents"]}>
                <ActivityHistory activity_history={history} />
              </div>
            </div>
          ) : null}
          <SinglePersonTitle title={"MY GAME"} subTitle={"マイゲーム"} />
          {game && game.length > 0 ? (
            <div className={styles["introduction__item"]}>
              <div className={styles["introduction__item__contents"]}>
                <MainGame games={game} />
              </div>
            </div>
          ) : (
            <NoData text={"マイゲームの情報がありません"} />
          )}
          {school ? (
            <div className={styles["introduction__item"]}>
              <SinglePersonTitle title={"SCHOOL"} subTitle={"所属校"} />
              <div className={styles["introduction__item__contents"]}>
                <Affiliation
                  name={school.name}
                  link={school.link}
                  img={school.img?.thumbnail.url}
                  color={school.color}
                />
              </div>
              {course ? (
                <p className={styles["school-course"]}>{course.name}</p>
              ) : null}
            </div>
          ) : null}
          {team && team.link ? (
            <div className={styles["introduction__item"]}>
              <SinglePersonTitle title={"TEAM"} subTitle={"チーム"} />
              <div className={styles["introduction__item__contents"]}>
                <Affiliation
                  name={team.name}
                  link={team.link}
                  img={team.img?.thumbnail?.url}
                  color={team.color}
                />
                {members && members.length ? (
                  <TeamMembers members={members} />
                ) : null}
              </div>
            </div>
          ) : null}
          <div className={styles["introduction__item"]}>
            <SinglePersonTitle title={"PROFILE"} subTitle={"プロフィール"} />
            <div className={styles["introduction__item__contents"]}>
              <Profile
                birthplace={birthplace}
                height={height}
                weight={weight}
                hobby={hobby}
              />
            </div>
          </div>
        </div>
      </article>
      {id &&
      isModalShown == true &&
      showOfferButton == true &&
      !isUsersOwnDBEntry ? (
        <OfferModalContainer
          id={id}
          ownerId={owner}
          ownerName={ownerName}
          ownerChatDenial={ownerChatDenial}
          recipientType="database_entries"
          handleClose={() => {
            setIsModalShown(false);
          }}
        />
      ) : null}

      <ContentReportModalContainer
        isModalShown={isReportModalShown}
        closeHandler={() => setIsReportModalShown(false)}
        pageType={PageTypes.Profile}
        contentName={name}
      />
    </>
  );
};

export default SinglePerson;
